import { joinUrl } from './http';
import { resolveCredential, type CredentialSource } from './types';

/**
 * Coarse, player-facing live state pushed by havik-streams over SSE. Mirrors
 * the `/v1/playback` outcome ladder so a pushed event and a follow-up
 * `/v1/playback` call always agree:
 *   live     ↔ 200 OK         (a serveable session exists — play now)
 *   upcoming ↔ 425 Too Early  (scheduled, not serving yet)
 *   ended    ↔ 503            (was live; media stopped, still in catchup)
 *   gone     ↔ 410 Gone       (ended past the catchup window)
 */
export type LiveState = 'live' | 'upcoming' | 'ended' | 'gone';

/**
 * Why a stream ended (havik-streams #191). `ended` alone cannot tell a viewer
 * whether the MATCH finished or only its stream dropped — two situations that
 * deserve opposite messaging:
 *
 * - `interrupted` — the match is still running; no session is serving (an
 *   ingest outage that outlived the bridge's reconnect grace). Play is
 *   expected to resume, so surface "reconnecting", never "this has ended".
 * - `match_ended` — the match itself finished. A real end.
 *
 * Absent on every other state, and absent entirely from servers predating the
 * field — so treat `undefined` as "unknown, assume a real end" (what clients
 * did before it existed).
 */
export type EndedReason = 'interrupted' | 'match_ended';

export interface LiveStateEvent {
  matchUrn: string;
  state: LiveState;
  /** Set only on `ended`; see {@link EndedReason}. */
  reason?: EndedReason;
  /** RFC3339 server timestamp the transition was stamped at. */
  serverTime: string;
}

export interface SubscribeLiveStateOptions {
  /** Base URL of the SSE endpoint, e.g. `https://events.feed.oddin-video.gg`. */
  eventsBaseUrl: string;
  matchUrn: string;
  credential: CredentialSource;
  /** Fired for the initial snapshot on connect and every subsequent transition. */
  onState: (ev: LiveStateEvent) => void;
  /**
   * Fired on a non-fatal subscription problem (network drop, HTTP error,
   * server lifetime-cap close). The client keeps retrying with backoff; this
   * is observational only — it never tears playback down.
   */
  onError?: (err: unknown) => void;
  /**
   * Fired on EVERY received frame, including heartbeat comments — a liveness
   * tick. Lets a caller treat the subscription as healthy (e.g. suspend
   * polling) while frames keep arriving, and fall back when they stop.
   */
  onAlive?: () => void;
  /** Reconnect backoff floor / ceiling (ms). Defaults: 1000 / 15000. */
  minBackoffMs?: number;
  maxBackoffMs?: number;
  /**
   * How long a request may stay silent before it is treated as dead and
   * reconnected. Applies to both phases: waiting for response headers, and
   * waiting for the next byte on an open connection. Default 45000 — two
   * missed 20s server heartbeats. Set `0` to disable the watchdog (a silent
   * connection then stays open forever, which is what this subscription did
   * before the watchdog existed).
   */
  idleTimeoutMs?: number;
}

/** Two missed 20s server heartbeats. See {@link SubscribeLiveStateOptions.idleTimeoutMs}. */
const DEFAULT_IDLE_TIMEOUT_MS = 45_000;

export interface LiveStateSubscription {
  /** Stop the subscription and abort the in-flight request. Idempotent. */
  close(): void;
}

/**
 * Subscribe to a match's live-state stream (`GET /v1/events/{urn}`).
 *
 * Uses `fetch` rather than the native `EventSource` because the endpoint
 * authenticates with the `x-api-key` HEADER, which `EventSource` cannot set.
 * The body is a `text/event-stream` we parse incrementally. The connection is
 * long-lived (the server caps it at ~30min and sends heartbeats); on any
 * close/error the client reconnects with exponential backoff and re-syncs from
 * the initial snapshot the server replays on connect. A connection that stops
 * delivering without closing is caught by the idle watchdog
 * (`idleTimeoutMs`) — see the note on it in `readStream`. A subscription
 * failure is always non-fatal to playback — callers keep the HLS staleness
 * watchdog as the layer-fallback.
 */
export function subscribeLiveState(opts: SubscribeLiveStateOptions): LiveStateSubscription {
  const minBackoff = Math.max(250, opts.minBackoffMs ?? 1000);
  const maxBackoff = Math.max(minBackoff, opts.maxBackoffMs ?? 15_000);
  // Not floored: a caller that asks for a very short window (a test) gets it —
  // the reconnect rate is bounded by minBackoff either way. 0/negative/NaN
  // disable the watchdog.
  const idleRaw = opts.idleTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MS;
  const idleMs = Number.isFinite(idleRaw) && idleRaw > 0 ? idleRaw : 0;
  const ctrl = new AbortController();
  let closed = false;

  const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      if (ctrl.signal.aborted) return resolve();
      const t = setTimeout(resolve, ms);
      ctrl.signal.addEventListener(
        'abort',
        () => {
          clearTimeout(t);
          resolve();
        },
        { once: true },
      );
    });

  const dispatch = (frame: string): void => {
    opts.onAlive?.(); // any frame (incl. a heartbeat comment) is a liveness tick
    let event = 'message';
    const data: string[] = [];
    for (const raw of frame.split('\n')) {
      const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
      if (line === '' || line.startsWith(':')) continue; // blank / comment (heartbeat)
      const i = line.indexOf(':');
      const field = i < 0 ? line : line.slice(0, i);
      let value = i < 0 ? '' : line.slice(i + 1);
      if (value.startsWith(' ')) value = value.slice(1);
      if (field === 'event') event = value;
      else if (field === 'data') data.push(value);
    }
    if (event !== 'state' || data.length === 0) return;
    try {
      const parsed = JSON.parse(data.join('\n')) as LiveStateEvent;
      if (parsed && typeof parsed.state === 'string') opts.onState(parsed);
    } catch {
      /* ignore a malformed frame; the next one re-syncs state */
    }
  };

  // SSE frame separator = a blank line. Accept LF, CRLF, and lone-CR servers
  // (per the spec, lines may end in \n, \r\n, or \r). Non-global so .exec always
  // scans from 0; it yields both the boundary index AND its length. A trailing
  // partial boundary (e.g. a chunk ending mid-"\r\n\r\n") simply stays buffered
  // until the next chunk completes it.
  const FRAME_BOUNDARY = /\r\n\r\n|\n\n|\r\r/;

  const readStream = async (
    body: ReadableStream<Uint8Array>,
    onData: () => void,
    onIdle: () => void,
  ): Promise<void> => {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let gotData = false;
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    // Idle watchdog. `reader.read()` below parks until the body yields, ends,
    // or errors — and a black-holed socket (laptop sleep, NAT rebind, proxy
    // half-close) does none of those: no FIN ever reaches us, so the read
    // never wakes and the subscription is permanently dead AND silent (no
    // onError, no reconnect). The server's ~30min lifetime cap only helps when
    // its close actually arrives. So: arm on open (this also covers
    // headers-then-no-body-ever), rearm on every chunk, and on expiry tear the
    // attempt down so run()'s loop reconnects.
    const rearmIdle = (): void => {
      if (!idleMs) return;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        onIdle(); // flags the attempt and aborts the request (socket teardown)
        // The abort alone is not enough: the body stream is already handed to
        // this reader, and cancel() is what resolves the parked read() as
        // `done` and unblocks the loop.
        void reader.cancel().catch(() => {});
      }, idleMs);
    };
    try {
      rearmIdle();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) return;
        if (value && value.length > 0) {
          rearmIdle();
          if (!gotData) {
            gotData = true;
            onData(); // first bytes flowed — the connection is genuinely healthy
          }
        }
        buf += decoder.decode(value, { stream: true });
        let m: RegExpExecArray | null;
        while ((m = FRAME_BOUNDARY.exec(buf)) !== null) {
          dispatch(buf.slice(0, m.index));
          buf = buf.slice(m.index + m[0].length);
        }
        if (closed) return;
      }
    } finally {
      clearTimeout(idleTimer);
      await reader.cancel().catch(() => {});
    }
  };

  const run = async (): Promise<void> => {
    let backoff = minBackoff;
    while (!closed) {
      // Per-attempt abort handle. The subscription-wide `ctrl` must stay
      // unaborted across reconnects (the catch below returns on it, and a
      // later fetch would be handed an already-aborted signal), so the idle
      // watchdog needs its own handle to tear down just THIS request. Chained
      // so close() still kills the in-flight one; the relay is removed in the
      // finally, or one closure would leak per reconnect on a long session.
      const attempt = new AbortController();
      const relayAbort = (): void => attempt.abort();
      ctrl.signal.addEventListener('abort', relayAbort, { once: true });
      // Set by whichever watchdog tore this attempt down, and reported after
      // the try/finally. Also suppresses the AbortError the teardown raises —
      // that error IS the teardown, and these messages name the cause.
      let idleReason: string | null = null;
      let connectTimer: ReturnType<typeof setTimeout> | undefined;
      const onIdle = (): void => {
        idleReason = `no data for ${idleMs}ms`;
        attempt.abort();
      };
      try {
        const cred = await resolveCredential(opts.credential);
        if (closed) return;
        const url = joinUrl(opts.eventsBaseUrl, `/v1/events/${encodeURIComponent(opts.matchUrn)}`);
        // Connect watchdog. `fetch` has no timeout of its own, so a socket that
        // black-holes during the HANDSHAKE (dropped SYN, captive portal, a NAT
        // that lost the mapping) parks this await forever — the same silent
        // death readStream's watchdog closes, one step earlier and not reachable
        // from there, since that one is only armed once a body exists. The two
        // belong together: a reconnect triggered BY the stream watchdog goes out
        // over a network that just changed under us, so it is exactly the
        // request most likely to hit a black hole. (A `credential` provider that
        // never settles is the caller's own promise and out of scope — aborting
        // cannot unpark the await above it.)
        if (idleMs) {
          connectTimer = setTimeout(() => {
            idleReason = `no response headers within ${idleMs}ms`;
            attempt.abort();
          }, idleMs);
        }
        const res = await fetch(url, {
          method: 'GET',
          headers: { 'x-api-key': cred.apiKey, accept: 'text/event-stream' },
          signal: attempt.signal,
          cache: 'no-store',
          credentials: 'omit',
          mode: 'cors',
        });
        clearTimeout(connectTimer); // headers are in; readStream arms its own
        if (closed) return;
        if (!res.ok || !res.body) {
          opts.onError?.(new Error(`live-state subscribe failed: HTTP ${res.status}`));
        } else {
          // Reset the backoff only once data actually flows (readStream's onData),
          // NOT on bare accept — an accept-then-immediate-close server would
          // otherwise reconnect in a tight loop with no backoff growth.
          await readStream(
            res.body,
            () => {
              backoff = minBackoff;
            },
            onIdle,
          );
        }
      } catch (err) {
        if (closed || ctrl.signal.aborted) return;
        // A watchdog teardown reports itself below. The AbortError it raises
        // here is the teardown, not a second failure — and this IS the usual
        // path in a real browser, where aborting errors the body stream and
        // rejects the parked read() before cancel() can resolve it as `done`.
        if (!idleReason) opts.onError?.(err);
      } finally {
        clearTimeout(connectTimer);
        ctrl.signal.removeEventListener('abort', relayAbort);
        attempt.abort(); // leave no request dangling behind a finished attempt
      }
      if (closed) return;
      if (idleReason) {
        opts.onError?.(new Error(`live-state subscribe idle: ${idleReason} — reconnecting`));
      }
      await sleep(backoff);
      backoff = Math.min(maxBackoff, backoff * 2);
    }
  };

  void run();

  return {
    close() {
      if (closed) return;
      closed = true;
      ctrl.abort();
    },
  };
}

/**
 * Derive the SSE endpoint base from the API base by prefixing the host with
 * `events.` — matching the deployed convention (`feed.<domain>` →
 * `events.<domain>`, served ALB-direct because CloudFront can't stream SSE).
 * Returns `undefined` if `baseUrl` can't be parsed.
 */
export function deriveEventsBaseUrl(baseUrl: string): string | undefined {
  try {
    const u = new URL(baseUrl);
    u.hostname = `events.${u.hostname}`;
    u.pathname = '';
    u.search = '';
    return u.origin;
  } catch {
    return undefined;
  }
}
